/*
  Included in this file:
  - getFocusableElements
  - trapFocus
  - removeTrapFocus
  - debounce
  - throttle
  - fetchConfig
  - formatMoney
*/

const trapFocusHandlers = {};

/**
 * Get all focusable elements within a container
 * @param container
 * @returns {*[]}
 */
function getFocusableElements(container) {
  const focusableSelectors = [
    "a[href]",
    "button:enabled",
    "[tabindex]:not([tabindex^='-'])",
    "[draggable]",
    "area",
    "input:not([type=hidden]):enabled",
    "select:enabled",
    "textarea:enabled",
    "object",
    "iframe"
  ];

  return [...container.querySelectorAll(focusableSelectors.join(","))];
}

/**
 * Trap focus within a container element
 * @param container
 * @param elementToFocus
 */
function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = event => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = event => {
    if (event.code.toUpperCase() !== 'TAB') return;

    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();

      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();

      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

/**
 * Remove focus trap
 * @param elementToFocus
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/**
 * Debounce function
 * @param fn
 * @param wait
 * @returns {function(...[*]=): void}
 */
function debounce(fn, wait) {
  let t;

  return (...args) => {
    clearTimeout(t);

    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Throttle function
 * @param fn
 * @param delay
 * @returns {function(...[*]=): void|*}
 */
function throttle(fn, delay) {
  let lastCall = 0;

  return function (...args) {
    const now = new Date().getTime();

    if (now - lastCall < delay) return;

    lastCall = now;

    return fn(...args);
  };
}

/**
 * Fetch configuration
 * @param type
 * @returns {{method: string, headers: {"Content-Type": string, Accept: string}}}
 */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`
    },
  };
}

/**
 * Formats a number of cents into a currency string using a Shopify format string.
 * @param cents - The number of cents to format.
 * @param format - The format string to use.
 * @returns The formatted currency string.
 */
function formatMoney(cents, format) {
  const placeholderRegex = /{{\s*(\w+)\s*}}/;
  const formatString = format || moneyFormat;

  /**
   * Formats a number of cents into a currency string using the provided
   * precision, thousands separator, and decimal separator.
   * @param number - The number of cents to format.
   * @param precision - The number of decimal places to include.
   * @param thousands - The character to use as the thousands' separator.
   * @param decimal - The character to use as the decimal separator.
   * @returns The formatted currency string.
   */
  function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
    if (isNaN(number) || number == null) return "0";

    const numString = (number / 100.0).toFixed(precision);
    const parts = numString.split(".");
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
    const cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  return formatString.replace(placeholderRegex, (match, placeholder) => {
    switch (placeholder) {
      case "amount":
        // Ex.	1,134.65
        return formatWithDelimiters(cents, 2);
      case "amount_no_decimals":
        // Ex. 1,135
        return formatWithDelimiters(cents, 0);
      case "amount_with_comma_separator":
        // Ex. 1.134,65
        return formatWithDelimiters(cents, 2, ".", ",");
      case "amount_no_decimals_with_comma_separator":
        // Ex. 1.135
        return formatWithDelimiters(cents, 0, ".", ",");
      case "amount_with_apostrophe_separator":
        // Ex. 1'134.65
        return formatWithDelimiters(cents, 2, "'", ".");
      case "amount_no_decimals_with_space_separator":
        // Ex. 1 135
        return formatWithDelimiters(cents, 0, " ");
      case "amount_with_space_separator":
        // 1 134,65
        return formatWithDelimiters(cents, 2, " ", ",");
      case "amount_with_period_and_space_separator":
        // 1 134.65
        return formatWithDelimiters(cents, 2, " ", ".");
      default:
        return match;
    }
  });
}

/**
 * Sets a cookie with the specified name, value, and expiration in days.
 * @param name - The name of the cookie.
 * @param value - The value of the cookie.
 * @param days - The number of days until the cookie expires.
 */
function setCookie(name, value, days) {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + encodeURIComponent(value) + ";" + expires + ";path=/";
}

/**
 * Initialize autoplay for videos that should only play when in view
 */
function initialiseVideoAutoplay() {
  const videos = document.querySelectorAll('video');
  if (!videos.length || !('IntersectionObserver' in window)) return;

  const visibilityThreshold = 0.5;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      const isVisibleEnough = entry.intersectionRatio >= visibilityThreshold;

      if (!isVisibleEnough) {
        if (!video.paused && !video.ended) {
          video.dataset.resumeWhenVisible = 'true';
          video.pause();
        }
        return;
      }

      if (video.dataset.resumeWhenVisible === 'true' || video.autoplay || video.loop) {
        video.play().catch(() => { });
        delete video.dataset.resumeWhenVisible;
      }
    });
  }, { threshold: [0, visibilityThreshold, 1] });

  videos.forEach((video) => observer.observe(video));
}

/**
 * Check if the browser has reduced motion preference enabled
 * @returns {boolean}
 */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Pause all autoplaying videos if reduced motion is preferred
 */
function pauseVideosonReducedMotion() {
  if (prefersReducedMotion()) {
    const videos = document.querySelectorAll('video[autoplay]');
    videos.forEach(video => video.pause());
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initialiseVideoAutoplay();
  pauseVideosonReducedMotion();
});